
import { useState } from 'react';
import { useAuth } from '@/context/AuthProvider';
import { usePayment } from '@/context/PaymentContext';
import { checkAndAutoProcessTransfers } from '@/utils/emailService';
import { secureRetrieve } from '@/utils/localStorageService';
import { DebitCard, Transaction } from '@/types/payment';
import { toast } from 'sonner';

export const useAutoClaimTransfers = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { user } = useAuth();
  const { addTransaction, debitCards } = usePayment();
  
  const checkPendingTransfers = async () => {
    if (!user?.email) {
      toast.error('Email address required', {
        description: 'Please ensure you have a valid email address'
      });
      return;
    }
    
    setIsProcessing(true);
    
    try {
      // First check if user has any verified cards
      const savedCards = await secureRetrieve<DebitCard[]>('user_debit_cards') || [];
      const hasVerifiedCard = savedCards.some(card => card.verified);
      
      if (!hasVerifiedCard) {
        toast.error('No verified card available', {
          description: 'Please add and verify a card to receive transfers automatically'
        });
        return;
      }
      
      const result = await checkAndAutoProcessTransfers(user.email);
      
      if (result.processed) {
        // Add transactions to history
        result.paymentIds.forEach(id => {
          const paymentData = localStorage.getItem(`pending_payment_${id}`);
          if (!paymentData) return;
          
          const payment = JSON.parse(paymentData);
          
          const transaction: Transaction = {
            id,
            amount: payment.amount,
            fee: payment.amount * 0.05,
            recipient: user.email,
            recipientEmail: user.email,
            sender: payment.sender,
            senderEmail: payment.senderEmail || 'unknown@example.com',
            status: 'completed',
            method: 'email_auto', // Fixed transaction method
            timestamp: payment.processedAt || new Date().toISOString(),
            metadata: {
              auto_processed: true,
              card_last4: savedCards[0]?.lastFourDigits
            }
          };
          
          addTransaction(transaction);
        });
        
        toast.success(`${result.paymentIds.length} pending transfers automatically processed`, {
          description: `Total amount: $${result.totalAmount.toFixed(2)} has been credited to your verified card`
        });
      }
    } catch (error) {
      console.error('Error checking transfers:', error);
      toast.error('Could not process transfers', {
        description: 'Please try again later or contact support'
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  return {
    checkPendingTransfers,
    isProcessing
  };
};
